import { mixins } from 'vue-class-component';
import { Component, Prop, Watch } from 'vue-property-decorator';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import EntitySelect from '../entity-select/entity-select';
import PaginationInterface from '@core/interfaces/pagination-interface';
import AuthorizationHelper from '@auth/services/authorization.helper';
import { ConfirmationModalConfig } from '@shared/interfaces/confirmation-modal-config';

const EntityTable = () => import('@shared/components/tables/entity-table/entity-table.vue');

import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import { Config } from '@shared/interfaces/config';
import DrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import Pluralize from 'pluralize';
import { Order } from '@accounting/interfaces/order';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import EntityFormProvider from '@shared/components/forms/auto-form/entity-form-provider/entity-form-provider';
import moment from 'moment';

@Component({
  name: 'bpc-entity-select-table',
  components: {
    'v-select': vSelect,
    'bpc-entity-table': EntityTable,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bpc-drawer-modal': DrawerModal,
    'bpc-std-form': StdForm,
  },
})
export default class EntitySelectTable extends mixins(EntitySelect) {
  $refs: EntitySelect['$refs'] & {
    softDeleteConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    removeConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    drawerStdForm: InstanceType<typeof StdForm> & {
      saveEntity(): Promise<BPCGenericApiResponse>;
      formModel: any;
      onDeleteMediaFile(): void;
      searchEntity(): void;
      extraData: string | AnyEntity;
      formProvider: EntityFormProvider;
      prepareToSave(entity: any): AnyEntity;
    };
  };

  @Prop({ type: Boolean, default: true }) readonly withDelete: boolean;
  @Prop({ type: Boolean, default: true }) readonly withRemove: boolean;
  @Prop({ type: String, default: 'default' }) readonly tableDisplayConfigName: string;

  public searchModel = null; // Only used when the displayType is table
  public auxId = null;
  public tableFields = [];

  localPagination: PaginationInterface = {
    local: true,
    current_page: 1,
    last_page: 1,
    per_page: 10,
    from: 0,
    to: 0,
    total: 0,
  };

  public defaultRowActions = {
    remove: {
      activated: false,
      type: 'action',
      icon: 'times',
      show: true,
      event: 'remove',
      text: 'remove',
      permissions: [],
    },
    softDelete: {
      activated: false,
      type: 'action',
      icon: 'trash',
      show: true,
      event: 'soft-delete',
      text: 'soft-delete',
      permissions: [],
    },
  };

  getSoftDeleteConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: this.selectEntity + '-soft-delete-modal',
      title: this.$t('global.modals.soft-delete.title', {
        entityName: this.$t('global.models.' + this.selectEntity + '.name'),
        entityPluralName: this.$t('global.models.' + this.selectEntity + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.selectEntity + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.selectEntity + '.thisPluralName'),
      }),
      body: this.$t('global.modals.soft-delete.body', {
        entityName: this.$t('global.models.' + this.selectEntity + '.name'),
        entityPluralName: this.$t('global.models.' + this.selectEntity + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.selectEntity + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.selectEntity + '.thisPluralName'),
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.move_to_trash') as string,
      acceptButtonVariant: 'danger',
    };
  }

  getRemoveConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: this.selectEntity + '-unlink-modal',
      title: this.$t('global.modals.unlink.title', {
        entityName: this.$t('global.models.' + this.selectEntity + '.name'),
        entityPluralName: this.$t('global.models.' + this.selectEntity + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.selectEntity + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.selectEntity + '.thisPluralName'),
      }),
      body: this.$t('global.modals.unlink.body', {
        entityName: this.$t('global.models.' + this.selectEntity + '.name'),
        entityPluralName: this.$t('global.models.' + this.selectEntity + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.selectEntity + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.selectEntity + '.thisPluralName'),
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.unlink') as string,
      acceptButtonVariant: 'danger',
    };
  }

  constructor() {
    super();
    this.randomId = this.makeId(10);
  }
  mounted() {
    this.$root.$on('calculateTotalPrice', event => {
      setTimeout(() => {
        this.calculateTotalPrice();
      });
    });
    if (this.withRemove && !this.disabled) {
      this.defaultRowActions.remove.activated = true;
    }
    if (this.withDelete && !this.disabled) {
      this.defaultRowActions.softDelete.activated = true;
    }
    if (this.$eventsService.events[this.selectEntity]) {
      this.$eventsService.events[this.selectEntity].mounted(this);
    }
    if (this.model) {
      this.localPagination.total = this.model.length;
      this.model.forEach(item => {
        item.actions = JSON.parse(JSON.stringify(this.defaultRowActions));
      });
    }
    this.getFields();
  }

  destroyed() {
    if (this.$eventsService.events[this.selectEntity]) {
      this.$eventsService.events[this.selectEntity].destroy(this);
    }
    this.$root.$off('refresh-table-after-add-orders-to-pos');
  }

  onChange($event) {
    this.addToModel($event[0]);
    this.searchModel = null;
    if (this.eventsConfig && this.eventsConfig.emit) {
      this.eventsConfig.emit.forEach(event => {
        this.$root.$emit(event, $event);
      });
    }
  }

  onClickDisplay($event) {
    console.log('EVENT ', $event);
    this.drawerEntity = $event.item;
    this.showEditDrawerForm = true;
  }

  public showEditDrawerForm: boolean = false;
  public isSavingDrawerForm: boolean = false;
  public disabledDrawerForm = true; // For read only mode when click in code column
  public drawerEntity: any = null;
  public drawerKey = 0;
  get drawerPermissionEntityNameToUse() {
    return Pluralize.isSingular(this.selectEntity)
      ? Pluralize(this.selectEntity)
      : this.selectEntity;
  }
  get drawerFormTitle() {
    return (
      this.$t('global.modals.edit') + ' ' + this.$t('global.models.' + this.selectEntity + '.name')
    );
  }
  get drawerFormConfigName() {
    let formConfigName;
    switch (this.selectEntity) {
      case 'order':
        formConfigName = 'edit-order';
        break;
      default:
        formConfigName = 'edit';
    }
    return formConfigName;
  }
  onDrawerFormEdit() {
    this.disabledDrawerForm = false;
  }
  onDrawerFormSave() {
    this.$refs.drawerStdForm.saveEntity();
  }
  onDrawerFormClose() {
    this.drawerKey++;
    this.isSavingDrawerForm = false;
    this.showEditDrawerForm = false;
  }
  onDrawerFormSaved() {
    this.drawerKey++;
    this.isSavingDrawerForm = false;
    this.showEditDrawerForm = false;
    if (this.selectEntity == 'order') {
      const index = this.model.findIndex(item => (item.id = this.drawerEntity.id));
      this.model[index] = this.drawerEntity;
      this.$root.$emit('calculateTotalAmountFromSelectTableToInvoiceTable');
    }
  }
  onDrawerSavingEntity() {
    this.isSavingDrawerForm = true;
  }

  get drawerEditBookingStoreName() {
    const initial =
      this.selectEntity === 'booking' && this.drawerEntity?.id
        ? `booking_${this.drawerEntity.id}`
        : 'booking';
    return `${initial}_${this.deep}`;
  }

  calculateTotalPrice() {
    const orderItems = this.$refs.drawerStdForm.formModel.orderItems;
    let totalPrice: number = 0;
    let totalTax: number = 0;
    let totalPriceExcludingTax: number = 0;
    orderItems.forEach(item => {
      const itemTotalPrice = item.price * item.quantity;
      totalPrice += itemTotalPrice;
      totalTax += itemTotalPrice - itemTotalPrice / (1 + item.tax_rate / 100);
      totalPriceExcludingTax = totalPrice - totalTax;
    });
    this.$set(
      this.$refs.drawerStdForm.formModel,
      'total_price',
      Number.isNaN(totalPrice) ? null : totalPrice.toFixed(2),
    );
    this.$set(
      this.$refs.drawerStdForm.formModel,
      'total_tax',
      Number.isNaN(totalTax) ? null : totalTax.toFixed(2),
    );
    this.$set(
      this.$refs.drawerStdForm.formModel,
      'total_excluding_tax_price',
      Number.isNaN(totalPriceExcludingTax) ? null : totalPriceExcludingTax.toFixed(2),
    );
  }

  private addToModel(item: any) {
    setTimeout(() => {
      let auxElement = null;
      if (this.model) {
        auxElement = this.model.find(element => {
          return element.id == item.id;
        });
      }
      if (!auxElement) {
        item.actions = JSON.parse(JSON.stringify(this.defaultRowActions));
        if (!this.model) {
          this.model = [item];
        } else {
          this.model.push(item);
        }
        this.updateTotal();
      }
    });
  }

  /** For selection multiple in the seatch table */
  addSelected() {
    const items = this.$refs[this.tableComponentName]['selection'];
    const selectedItems = this.$refs[this.tableComponentName].$refs.entityTable.items.filter(
      item => items.includes(item['id']) === true,
    );
    selectedItems.forEach(item => {
      this.addToModel(item);
    });

    this.$bvModal.hide(`${this.randomId}-table-search-modal`);
    this.updateTotal();
    this.$root.$emit('calculateTotalAmountFromSelectTableToInvoiceTable');
  }
  private updateTotal() {
    setTimeout(() => {
      if (this.model) {
        this.localPagination.total = this.model.length; // Need it for the well behavior of the pagination. We have update each time that we modify the model
      }
    });
  }

  onRowAction($event: { event: string; item: any }) {
    switch ($event.event) {
      case 'remove':
        this.onRemove($event.item);
        break;
      case 'soft-delete':
        this.onSoftDelete($event.item);
        break;
    }
    console.log('Row action event >>> ' + $event);
  }

  onSoftDelete($event) {
    this.auxId = $event['item']['id'];
    this.$refs.softDeleteConfirmationModal.onOpenModal($event['item']['id']);
  }
  onSuccessSoftDelete() {
    this.remove({ entityIdOrIds: this.auxId });
    this.$refs.softDeleteConfirmationModal.onCloseModal();
  }
  onRemove($event) {
    this.$refs.removeConfirmationModal.onOpenModal($event['item']['id']);
  }
  remove($event) {
    this.model.forEach((element, index) => {
      if (element['id'] == $event['entityIdOrIds']) {
        this.model.splice(index, 1);
      }
    });
    if (this.selectEntity == 'order') {
      this.$root.$emit('calculateTotalAmountFromSelectTableToInvoiceTable');
    }
    this.$refs.removeConfirmationModal.onCloseModal();
  }

  formatDate(value: string): string {
    return this.$bpcHelper.formatDateV2(value);
  }
  formatTime(value: string): string {
    return this.$bpcHelper.formatTimeV2(value);
  }

  async getFields() {
    let config: Config;
    await this.$bpcConfig.getEntityConfig(this.selectEntity).subscribe(res => {
      config = res.data;
      if (config && config.displays && config.displays[this.tableDisplayConfigName]) {
        this.tableFields = config.displays[this.tableDisplayConfigName]['fields'];
        this.addActionColumns();
      }
    });
  }

  addActionColumns(): void {
    this.tableFields.push({
      order: 100,
      key: 'actions',
      label: 'Actions',
      class: '',
      help: '',
      thStyle: { width: '50px' },
      tdStyle: { width: '50px' },
      sortable: false,
      displayType: 'actions',
    });
  }

  onFormSaved($event) {
    this.forceSelectElement($event.data);
    this.isSavingForm = false;
    this.showDrawerForm = false;
    this.$refs.provider.getEntities();
  }

  /** For simple select in the search table */
  forceSelectElement(selectedItem) {
    let search = false;
    if (this.model) {
      search = this.model.find(item => {
        return item['id'] === selectedItem['id'];
      });
    }
    if (!search) {
      this.onChange([selectedItem]);
    }
  }

  onErrorSavingEntity() {
    this.isSavingForm = false;
  }
}
